import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Input from '../components/Input';
import { registerUser } from './RegisterService';
import './Register.css';

const STEPS = {
  USER: 1,
  TYPE: 2,
  HUB: 3
};

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  password: '',
  registration_type: '',
  nida: '',
  hub: {
    name: '',
    title: '',
    location: '',
    phone: '',
    email: ''
  }
};

const RegisterPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [step, setStep] = useState(STEPS.USER);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const isIndividual = formData.registration_type === 'individual';
  const isHub = formData.registration_type === 'hub';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHubChange = (e) => {
    const { name, value } = e.target;
    const hubField = name.replace(/^hub_/, '');
    setFormData((prev) => ({
      ...prev,
      hub: { ...prev.hub, [hubField]: value }
    }));
  };

  const handleTypeChange = (type) => {
    setFormData((prev) => ({ ...prev, registration_type: type }));
  };

  const validateUserStep = () => {
    if (!formData.name.trim()) return 'Please enter your name.';
    if (!formData.email.trim()) return 'Please enter your email.';
    if (!formData.phone.trim()) return 'Please enter your phone number.';
    if (!formData.password.trim()) return 'Please enter a password.';
    return null;
  };

  const validateTypeStep = () => {
    if (!formData.registration_type) return 'Please select Individual or From hub.';
    if (isIndividual && !formData.nida.trim()) return 'Please enter your NIDA number.';
    return null;
  };

  const validateHubStep = () => {
    const h = formData.hub;
    if (!h.name.trim()) return 'Please enter the hub name.';
    if (!h.title.trim()) return 'Please enter the hub title.';
    if (!h.location.trim()) return 'Please enter the hub location.';
    if (!h.phone.trim()) return 'Please enter the hub phone number.';
    if (!h.email.trim()) return 'Please enter the hub email.';
    return null;
  };

  const handleNextFromUser = (e) => {
    e.preventDefault();
    const err = validateUserStep();
    if (err) {
      Swal.fire({ icon: 'warning', title: 'Missing information', text: err, confirmButtonColor: '#2563eb', width: 300 });
      return;
    }
    setStep(STEPS.TYPE);
  };

  const handleNextFromType = (e) => {
    e.preventDefault();
    const err = validateTypeStep();
    if (err) {
      Swal.fire({ icon: 'warning', title: 'Missing information', text: err, confirmButtonColor: '#2563eb', width: 300 });
      return;
    }
    if (isHub) setStep(STEPS.HUB);
  };

  const submitRegistration = async () => {
    if (isIndividual) {
      const err = validateTypeStep();
      if (err) {
        Swal.fire({ icon: 'warning', title: 'Missing information', text: err, confirmButtonColor: '#2563eb', width: 300 });
        return;
      }
    } else if (isHub) {
      const err = validateHubStep();
      if (err) {
        Swal.fire({ icon: 'warning', title: 'Missing information', text: err, confirmButtonColor: '#2563eb', width: 300 });
        return;
      }
    }

    setLoading(true);
    try {
      const response = await registerUser(formData);

      if (response.success) {
        await Swal.fire({
          icon: 'success',
          title: 'Registration Successful!',
          text: 'Your account has been created successfully. Redirecting to login...',
          confirmButtonColor: '#2563eb',
          timer: 2000,
          showConfirmButton: true,
          width: 300,
          customClass: {
            popup: 'swal2-small',
            title: 'swal2-title-small',
            content: 'swal2-content-small'
          }
        });
        navigate('/login');
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Registration Failed',
          text: response.message || 'Registration failed. Please try again.',
          confirmButtonColor: '#2563eb',
          width: 300,
          customClass: {
            popup: 'swal2-small',
            title: 'swal2-title-small',
            content: 'swal2-content-small'
          }
        });
      }
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.errorMessage || 'An error occurred during registration. Please try again.',
        confirmButtonColor: '#2563eb',
        width: 300,
        customClass: {
          popup: 'swal2-small',
          title: 'swal2-title-small',
          content: 'swal2-content-small'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === STEPS.USER) handleNextFromUser(e);
    else if (step === STEPS.TYPE && isHub) handleNextFromType(e);
    else if (step === STEPS.TYPE && isIndividual) submitRegistration();
    else if (step === STEPS.HUB) submitRegistration();
  };

  const stepLabel =
    step === STEPS.USER ? 'Step 1 of 3 — Your details' :
    step === STEPS.TYPE ? (isHub ? 'Step 2 of 3 — Registration type' : 'Step 2 of 2 — Registration type') :
    'Step 3 of 3 — Hub details';

  return (
    <div className="register-container">
      <h2>Register</h2>
      <p className="register-step-label">{stepLabel}</p>

      <form onSubmit={handleSubmit} className="register-form">
        {step === STEPS.USER && (
          <>
            <Input
              label="Name"
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <Input
              label="Phone Number"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <Input
              label="Password"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
            <button type="submit" className="register-btn" disabled={loading}>
              Next
            </button>
          </>
        )}

        {step === STEPS.TYPE && (
          <>
            <div className="register-type-group">
              <span className="register-type-label">Are you registering as an individual or from a hub?</span>
              <div className="register-type-options register-type-options-stack">
                <label className="register-type-option">
                  <input
                    type="radio"
                    name="registration_type"
                    value="individual"
                    checked={isIndividual}
                    onChange={() => handleTypeChange('individual')}
                    disabled={loading}
                  />
                  Individual
                </label>
                <label className="register-type-option">
                  <input
                    type="radio"
                    name="registration_type"
                    value="hub"
                    checked={isHub}
                    onChange={() => handleTypeChange('hub')}
                    disabled={loading}
                  />
                  From hub
                </label>
              </div>
            </div>

            {isIndividual && (
              <Input
                label="NIDA"
                type="text"
                name="nida"
                value={formData.nida}
                onChange={handleChange}
                required
                disabled={loading}
              />
            )}

            <div className="register-actions">
              <button
                type="button"
                className="register-btn register-btn-secondary"
                onClick={() => setStep(STEPS.USER)}
                disabled={loading}
              >
                Back
              </button>
              {isIndividual ? (
                <button type="submit" className="register-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Registering...
                    </>
                  ) : (
                    'Register'
                  )}
                </button>
              ) : (
                <button type="submit" className="register-btn" disabled={loading || !isHub}>
                  Next
                </button>
              )}
            </div>
          </>
        )}

        {step === STEPS.HUB && (
          <>
            <div className="register-hub-section">
              <h3 className="register-hub-title">Hub details</h3>
              <Input
                label="Hub name"
                type="text"
                name="hub_name"
                value={formData.hub.name}
                onChange={handleHubChange}
                required
                disabled={loading}
              />
              <Input
                label="Hub title"
                type="text"
                name="hub_title"
                value={formData.hub.title}
                onChange={handleHubChange}
                required
                disabled={loading}
              />
              <Input
                label="Location"
                type="text"
                name="hub_location"
                value={formData.hub.location}
                onChange={handleHubChange}
                required
                disabled={loading}
              />
              <Input
                label="Hub phone"
                type="tel"
                name="hub_phone"
                value={formData.hub.phone}
                onChange={handleHubChange}
                required
                disabled={loading}
              />
              <Input
                label="Hub email"
                type="email"
                name="hub_email"
                value={formData.hub.email}
                onChange={handleHubChange}
                required
                disabled={loading}
              />
            </div>

            <div className="register-actions">
              <button
                type="button"
                className="register-btn register-btn-secondary"
                onClick={() => setStep(STEPS.TYPE)}
                disabled={loading}
              >
                Back
              </button>
              <button type="submit" className="register-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Registering...
                  </>
                ) : (
                  'Register'
                )}
              </button>
            </div>
          </>
        )}

        <p className="register-switch-text">
          Already have an account? <Link to="/login" className="register-switch-link">Login</Link>
        </p>
      </form>
    </div>
  );
};

export default RegisterPage;
